/**
 * Tenant deletion error code → Polish message mapper.
 *
 * Backend error codes per AC-33 (spec docs/specifications/2026-05-04-tenant-deletion-archive/spec.md).
 * Backend error response shape: { error: { code: string, message: string } }
 */

export type TenantDeletionErrorCode =
  | "SELF_DELETION_FORBIDDEN"
  | "TENANT_NOT_FOUND"
  | "REASON_TOO_SHORT"
  | "REASON_TOO_LONG"
  | "TENANT_NAME_MISMATCH"
  | "TENANT_TOO_LARGE"
  | "INSUFFICIENT_PERMISSIONS"
  | "DELETION_FAILED"
  | "ENDPOINT_DEPRECATED";

const ERROR_CODE_MESSAGES: Record<TenantDeletionErrorCode, string> = {
  SELF_DELETION_FORBIDDEN:
    "Nie możesz usunąć tenanta do którego należysz lub należałeś.",
  TENANT_NOT_FOUND: "Tenant nie istnieje lub został już usunięty.",
  REASON_TOO_SHORT: "Powód musi mieć minimum 20 znaków.",
  REASON_TOO_LONG: "Powód nie może być dłuższy niż 500 znaków.",
  TENANT_NAME_MISMATCH: "Wpisana nazwa nie zgadza się z nazwą tenanta.",
  TENANT_TOO_LARGE:
    "Tenant jest zbyt duży na natychmiastowe usunięcie. Skontaktuj się z DevOps.",
  INSUFFICIENT_PERMISSIONS: "Brak uprawnień do tej operacji.",
  DELETION_FAILED:
    "Wystąpił błąd. Spróbuj ponownie lub skontaktuj się z DevOps.",
  ENDPOINT_DEPRECATED:
    "Endpoint został wycofany. Użyj nowego panelu administracyjnego.",
};

const DEFAULT_FALLBACK = "Wystąpił nieoczekiwany błąd. Spróbuj ponownie.";

/**
 * Map a backend error code to Polish user-facing message.
 * Returns a default fallback for unknown codes.
 */
export function mapTenantDeletionErrorCode(
  code: TenantDeletionErrorCode | string | undefined | null
): string {
  if (!code) return DEFAULT_FALLBACK;
  return ERROR_CODE_MESSAGES[code as TenantDeletionErrorCode] ?? DEFAULT_FALLBACK;
}

/**
 * Type guard for axios-like error shape: `{ response: { data: { error: { code: unknown } } } }`.
 * Avoids `as any` cast in `extractTenantDeletionErrorCode`.
 */
function isAxiosLikeError(
  e: unknown
): e is { response: { data: { error: { code: unknown } } } } {
  if (typeof e !== "object" || e === null) return false;
  const r = (e as { response?: unknown }).response;
  if (typeof r !== "object" || r === null) return false;
  const d = (r as { data?: unknown }).data;
  if (typeof d !== "object" || d === null) return false;
  const err = (d as { error?: unknown }).error;
  return typeof err === "object" && err !== null;
}

/**
 * Try to extract the backend error code from an axios-style error.
 * Returns undefined if the shape doesn't match.
 */
export function extractTenantDeletionErrorCode(
  error: unknown
): string | undefined {
  if (!isAxiosLikeError(error)) return undefined;
  const code = error.response.data.error.code;
  return typeof code === "string" ? code : undefined;
}
